"use client";

import {
  useState,
  useEffect,
  Suspense,
  lazy,
  useRef,
  useCallback,
} from "react";
import { useSearchParams } from "next/navigation";
import dynamic from "next/dynamic";
import Script from "next/script";
import Link from "next/link";
import { useUsage } from "@/lib/use-usage";
import { useTurnstile } from "@/lib/use-turnstile";
import { fetchWithTurnstile } from "@/lib/turnstile-fetch";

// Lazy load react-markdown (164KB) - only needed for AI summaries
const ReactMarkdown = dynamic(() => import("react-markdown"), {
  loading: () => (
    <div className="animate-pulse bg-slate-200 dark:bg-slate-700 h-32 rounded" />
  ),
  ssr: false,
});

// Lazy load modals - not needed on initial render
const WaitlistModal = dynamic(
  () =>
    import("@/components/waitlist-modal").then((m) => ({
      default: m.WaitlistModal,
    })),
  { ssr: false },
);
const PricingModal = dynamic(
  () =>
    import("@/components/pricing-modal").then((m) => ({
      default: m.PricingModal,
    })),
  { ssr: false },
);
const RestoreModal = dynamic(
  () =>
    import("@/components/restore-modal").then((m) => ({
      default: m.RestoreModal,
    })),
  { ssr: false },
);
const AutoTranslateModal = dynamic(
  () =>
    import("@/components/auto-translate-modal").then((m) => ({
      default: m.AutoTranslateModal,
    })),
  { ssr: false },
);
const FeedbackModal = dynamic(
  () =>
    import("@/components/feedback-modal").then((m) => ({
      default: m.FeedbackModal,
    })),
  { ssr: false },
);
const VpnSignInModal = dynamic(
  () =>
    import("@/components/vpn-signin-modal").then((m) => ({
      default: m.VpnSignInModal,
    })),
  { ssr: false },
);
const ProBadge = dynamic(
  () => import("@/components/pro-badge").then((m) => ({ default: m.ProBadge })),
  { ssr: false },
);

import { useSubscription } from "@/lib/use-subscription";
import { analytics } from "@/lib/analytics";
import { useFeatureFlagVariantKey } from "posthog-js/react";

// Languages offered for direct AI audio transcription (Whisper). Used when a video has no caption
// track in the language you want — or when YouTube's auto-captions are mislabeled (e.g. an Urdu
// lecture served as broken "English"). Picking one re-transcribes the audio directly in that language.
const TRANSCRIBE_LANGUAGES: { code: string; name: string }[] = [
  { code: "en", name: "English" },
  { code: "ur", name: "Urdu" },
  { code: "hi", name: "Hindi" },
  { code: "ar", name: "Arabic" },
  { code: "fa", name: "Persian" },
  { code: "bn", name: "Bengali" },
  { code: "pa", name: "Punjabi" },
  { code: "ta", name: "Tamil" },
  { code: "es", name: "Spanish" },
  { code: "pt", name: "Portuguese" },
  { code: "fr", name: "French" },
  { code: "de", name: "German" },
  { code: "it", name: "Italian" },
  { code: "ru", name: "Russian" },
  { code: "tr", name: "Turkish" },
  { code: "id", name: "Indonesian" },
  { code: "vi", name: "Vietnamese" },
  { code: "zh", name: "Chinese" },
  { code: "ja", name: "Japanese" },
  { code: "ko", name: "Korean" },
];

// Separate component for handling restored notification that requires useSearchParams
function RestoredNotification({ onRestored }: { onRestored: () => void }) {
  const searchParams = useSearchParams();

  useEffect(() => {
    if (searchParams.get("restored") === "1") {
      onRestored();
      // Clear the query param from URL
      window.history.replaceState({}, "", "/");
    }
  }, [searchParams, onRestored]);

  return null;
}

interface TranscriptSegment {
  text: string;
  offset: number;
  duration: number;
}

export default function Home() {
  const { containerId: turnstileContainerId, getToken: getTurnstileToken } = useTurnstile();
  const [url, setUrl] = useState("");
  const [videoId, setVideoId] = useState<string | null>(null);
  const [videoTitle, setVideoTitle] = useState<string | null>(null);
  const [channelName, setChannelName] = useState<string | null>(null);
  const [transcript, setTranscript] = useState<TranscriptSegment[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [errorHelpLink, setErrorHelpLink] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [showTimestamps, setShowTimestamps] = useState(false);
  const [showDownloadMenu, setShowDownloadMenu] = useState(false);

  // Transcript feedback state
  const [transcriptFeedbackRating, setTranscriptFeedbackRating] = useState<
    "positive" | "negative" | null
  >(null);
  const [transcriptFeedbackText, setTranscriptFeedbackText] = useState("");
  const [transcriptFeedbackSubmitted, setTranscriptFeedbackSubmitted] =
    useState(false);

  // Search state
  const [searchQuery, setSearchQuery] = useState("");
  const [currentMatchIndex, setCurrentMatchIndex] = useState(0);
  const currentMatchRef = useRef<HTMLElement>(null);

  // Language selection state
  interface AvailableLanguage {
    code: string;
    name: string;
    isAutoGenerated: boolean;
  }
  const [availableLanguages, setAvailableLanguages] = useState<AvailableLanguage[]>([]);
  const [selectedLanguage, setSelectedLanguage] = useState<string | undefined>(undefined);
  const [transcriptLanguage, setTranscriptLanguage] = useState<string>("en");
  const [loadingLanguages, setLoadingLanguages] = useState(false);

  // Batch mode state
  const [batchMode, setBatchMode] = useState(false);
  const [batchUrls, setBatchUrls] = useState("");
  const [batchResults, setBatchResults] = useState<
    Array<{
      url: string;
      videoId: string;
      title?: string;
      transcript: TranscriptSegment[];
      error?: string;
      status: "pending" | "loading" | "success" | "error";
    }>
  >([]);
  const [batchProcessing, setBatchProcessing] = useState(false);

  // AI Summary state
  const [summary, setSummary] = useState("");
  const [summarizing, setSummarizing] = useState(false);
  const [summaryError, setSummaryError] = useState("");
  const [summaryTruncation, setSummaryTruncation] = useState<{
    truncated: boolean;
    coveredMinutes: number;
    totalMinutes: number;
  } | null>(null);

  // Summary feedback state
  const [summaryFeedbackRating, setSummaryFeedbackRating] = useState<
    "positive" | "negative" | null
  >(null);
  const [summaryFeedbackText, setSummaryFeedbackText] = useState("");
  const [summaryFeedbackSubmitted, setSummaryFeedbackSubmitted] =
    useState(false);

  // Usage tracking (server-side)
  const { updateTranscriptUsage, updateSummaryUsage, remainingTranscripts } =
    useUsage();
  const [showWaitlist, setShowWaitlist] = useState(false);

  // Pro subscription
  const {
    authenticated,
    email: sessionEmail,
    hasPro,
    subscription,
    loading: subLoading,
    openPortal,
    logout,
    refresh: refreshSubscription,
  } = useSubscription();
  const [showPricing, setShowPricing] = useState(false);
  const [pricingSource, setPricingSource] = useState<
    "limit" | "pro_button" | "ai_summary" | "manual" | "no_captions"
  >("manual");
  const [showRestore, setShowRestore] = useState(false);
  const [restoredNotification, setRestoredNotification] = useState(false);

  // Hybrid approach: show both inline and sticky footer upgrade indicators
  // (Previously A/B tested - hybrid won on both engagement and upgrades)

  // Credit balance tracking
  const [creditBalance, setCreditBalance] = useState<number | null>(null);
  const [creditUsedNotification, setCreditUsedNotification] = useState(false);

  // Auto-translate coming soon modal
  const [showAutoTranslateModal, setShowAutoTranslateModal] = useState(false);

  // Feedback modal
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);
  const [feedbackDefaultTab, setFeedbackDefaultTab] = useState<"bug" | "feature" | "general">("general");

  // VPN sign-in modal
  const [showVpnSignIn, setShowVpnSignIn] = useState(false);
  const [autoTranslateTargetLang, setAutoTranslateTargetLang] = useState("");

  // Handle restored notification callback (from RestoredNotification component)
  const handleRestored = () => {
    setRestoredNotification(true);
    // Hide notification after 5 seconds
    setTimeout(() => setRestoredNotification(false), 5000);
  };

  // Listen for restore modal event
  useEffect(() => {
    const handleOpenRestore = () => setShowRestore(true);
    window.addEventListener("open-restore-modal", handleOpenRestore);
    // Deep link support (e.g. /?restore=1 from /developers)
    if (new URLSearchParams(window.location.search).get("restore") === "1") {
      setShowRestore(true);
    }
    return () =>
      window.removeEventListener("open-restore-modal", handleOpenRestore);
  }, []);

  // Fetch initial credit balance
  useEffect(() => {
    const fetchCredits = async () => {
      try {
        const response = await fetch("/api/credits");
        const data = await response.json();
        if (data.balance !== undefined) {
          setCreditBalance(data.balance);
        }
      } catch {
        // Silently fail - credits are optional
      }
    };
    fetchCredits();
  }, []);

  // Calculate stats
  const wordCount = transcript.reduce(
    (acc, seg) => acc + seg.text.split(/\s+/).length,
    0,
  );
  const readingTime = Math.ceil(wordCount / 200);
  const videoDuration =
    transcript.length > 0
      ? Math.ceil(
          (transcript[transcript.length - 1].offset +
            transcript[transcript.length - 1].duration) /
            1000 /
            60,
        )
      : 0;

  const extractVideoId = (url: string): string | null => {
    const patterns = [
      /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/|youtube\.com\/live\/|youtube\.com\/shorts\/)([^&\n?#]+)/,
      /^([a-zA-Z0-9_-]{11})$/,
    ];
    for (const pattern of patterns) {
      const match = url.match(pattern);
      if (match) return match[1];
    }
    return null;
  };

  const fetchVideoMetadata = async (
    videoId: string,
  ): Promise<{ title: string | null; channelName: string | null }> => {
    try {
      const response = await fetch(
        `https://www.youtube.com/oembed?url=https://www.youtube.com/watch?v=${videoId}&format=json`,
      );
      if (response.ok) {
        const data = await response.json();
        return {
          title: data.title || null,
          channelName: data.author_name || null,
        };
      }
    } catch {
      // Silently fail - metadata is optional
    }
    return { title: null, channelName: null };
  };

  const handleSubmit = async (e: React.FormEvent, langOverride?: string) => {
    e.preventDefault();
    setError("");
    setTranscript([]);
    setCopied(false);
    setSummary("");
    setSummaryError("");
    // Reset language states for new video (but not on language change)
    if (!langOverride) {
      setVideoId(null);
      setVideoTitle(null);
      setChannelName(null);
      setAvailableLanguages([]);
      setSelectedLanguage(undefined);
      setTranscriptLanguage("en");
    }
    // Reset feedback states
    setTranscriptFeedbackRating(null);
    setTranscriptFeedbackText("");
    setTranscriptFeedbackSubmitted(false);
    setSummaryFeedbackRating(null);
    setSummaryFeedbackText("");
    setSummaryFeedbackSubmitted(false);

    const extractedId = extractVideoId(url);
    if (!extractedId) {
      setError("Invalid YouTube URL. Please enter a valid YouTube video link.");
      return;
    }

    const langToUse = langOverride || selectedLanguage;
    analytics.trackTranscriptFetch(extractedId);
    setLoading(true);

    // Start fetching languages in parallel (fire-and-forget, non-blocking)
    // Always fetch for new videos (when !langOverride), languages were just cleared above
    if (!langOverride) {
      setLoadingLanguages(true);
      fetch(`/api/languages?videoId=${extractedId}`)
        .then((res) => (res.ok ? res.json() : null))
        .then((data) => {
          if (data?.languages) setAvailableLanguages(data.languages);
        })
        .catch(() => {}) // Silently fail - language selection is optional
        .finally(() => setLoadingLanguages(false));
    }

    try {
      const langParam = langToUse ? `&lang=${langToUse}` : "";
      // Retries once with a fresh token on a Turnstile rejection (see turnstile-fetch.ts).
      const { response, data, retried: turnstileRetried } = await fetchWithTurnstile(
        `/api/transcript?videoId=${extractedId}${langParam}`,
        {},
        getTurnstileToken,
      );

      if (!response.ok) {
        if (response.status === 403 && data.vpn_blocked) {
          setShowVpnSignIn(true);
          setLoading(false);
          return;
        }
        if (response.status === 429) {
          analytics.trackRateLimitHit("transcript");
          // Note: trackUpgradeModalShown is called by PricingModal's useEffect
          setPricingSource("limit");
          setShowPricing(true);
        }
        const fetchErr = new Error(String(data.error || "Failed to fetch transcript")) as Error & {
          turnstileReason?: string;
          turnstileRetried?: boolean;
        };
        if (data.turnstile_failed) {
          // Both attempts were rejected — carry the server's reason into the error event.
          fetchErr.turnstileReason = String(data.reason || "unknown");
          fetchErr.turnstileRetried = turnstileRetried;
        }
        throw fetchErr;
      }

      setVideoId(extractedId);
      setTranscript(data.transcript);
      // Set transcript language from response
      if (data.lang) {
        setTranscriptLanguage(data.lang);
      }
      const transcriptWordCount = data.transcript.reduce(
        (acc: number, seg: TranscriptSegment) =>
          acc + seg.text.split(/\s+/).length,
        0,
      );

      // Fetch video metadata for analytics and AI summary context
      const metadata = await fetchVideoMetadata(extractedId);
      setVideoTitle(metadata.title);
      setChannelName(metadata.channelName);

      analytics.trackTranscriptSuccess(
        extractedId,
        transcriptWordCount,
        data.cached || false,
        metadata.title ?? undefined,
        metadata.channelName ?? undefined,
      );
      if (data.usage) {
        updateTranscriptUsage(data.usage);
      }
      // Update credit balance and show notification if credit was used
      if (data.credits) {
        setCreditBalance(data.credits.balance);
        if (data.credits.usedCredit) {
          analytics.trackCreditUsed(extractedId, data.credits.balance);
          setCreditUsedNotification(true);
          setTimeout(() => setCreditUsedNotification(false), 4000);
        }
      }
    } catch (err) {
      const rawError =
        err instanceof Error ? err.message : "Something went wrong";
      // Provide friendlier messages for common network errors
      let errorMessage = rawError;
      let helpLink: string | null = null;
      if (rawError === "Load failed" || rawError === "Failed to fetch") {
        errorMessage = "Network error – couldn't reach YouTube. This may be due to a slow connection or regional restrictions.";
        helpLink = "/blog/fix-youtube-transcript-not-loading";
      } else if (rawError.includes("NetworkError") || rawError.includes("network")) {
        errorMessage = "Network error – please check your internet connection and try again.";
        helpLink = "/blog/fix-youtube-transcript-not-loading";
      }
      // Classify error for PostHog filtering
      let errorCategory = "fetch_error";
      const turnstileInfo = err as { turnstileReason?: string; turnstileRetried?: boolean };
      if (rawError.includes("Verification failed")) {
        errorCategory = "turnstile_failed";
      } else if (rawError.includes("livestream")) {
        errorCategory = "livestream_pending";
      } else if (rawError.includes("VPN") || rawError.includes("Proxy detected")) {
        errorCategory = "vpn_blocked";
      } else if (rawError.includes("blocking free AI transcription")) {
        errorCategory = "ai_blocked_free";
      } else if (rawError.includes("No transcript") || rawError.includes("disabled captions") || rawError.includes("unavailable") || rawError.includes("private")) {
        errorCategory = "no_captions";
      } else if (rawError.includes("Daily limit") || rawError.includes("rate")) {
        errorCategory = "rate_limited";
      } else if (rawError === "Load failed" || rawError === "Failed to fetch" || rawError.includes("NetworkError") || rawError.includes("network")) {
        errorCategory = "network_error";
      }
      analytics.trackTranscriptError(
        extractedId,
        rawError,
        errorCategory,
        errorCategory === "turnstile_failed"
          ? { turnstile_reason: turnstileInfo.turnstileReason ?? "unknown", turnstile_retried: turnstileInfo.turnstileRetried === true }
          : undefined,
      );
      setError(errorMessage);
      setErrorHelpLink(helpLink);
    } finally {
      setLoading(false);
    }
  };

  // Handle language change - re-fetch transcript with selected language
  const handleLanguageChange = async (newLang: string) => {
    // Check if this is a translate option (coming soon feature)
    if (newLang.startsWith("translate-")) {
      const langName = newLang.replace("translate-", "");
      handleAutoTranslateClick(langName);
      return;
    }
    if (!videoId || newLang === transcriptLanguage) return;
    setSelectedLanguage(newLang);
    // Create a synthetic event for handleSubmit
    const syntheticEvent = { preventDefault: () => {} } as React.FormEvent;
    await handleSubmit(syntheticEvent, newLang);
  };

  const handleSummarize = async () => {
    if (!videoId) return;
    analytics.trackSummaryClick(videoId);
    setSummarizing(true);
    setSummaryError("");
    setSummary("");
    setSummaryTruncation(null);
    // Reset feedback state
    setSummaryFeedbackRating(null);
    setSummaryFeedbackText("");
    setSummaryFeedbackSubmitted(false);

    const plainText = transcript.map((seg) => seg.text).join(" ").trim();

    if (!plainText) {
      setSummaryError("No transcript text available to summarize.");
      setSummarizing(false);
      return;
    }

    try {
      const { response, data } = await fetchWithTurnstile(
        "/api/summarize",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            transcript: plainText,
            videoId,
            videoTitle,
            channelName,
          }),
        },
        getTurnstileToken,
      );

      if (!response.ok) {
        if (response.status === 403 && data.vpn_blocked) {
          setShowVpnSignIn(true);
          setSummarizing(false);
          return;
        }
        if (response.status === 429) {
          analytics.trackRateLimitHit("summary");
          // Note: trackUpgradeModalShown is called by PricingModal's useEffect
          setPricingSource("ai_summary");
          setShowPricing(true);
        }
        throw new Error(data.error || "Failed to generate summary");
      }

      setSummary(data.summary);
      if (data.truncation) {
        setSummaryTruncation(data.truncation);
      }
      analytics.trackSummarySuccess(videoId, data.cached || false);
      if (data.usage) {
        updateSummaryUsage(data.usage);
      }
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : "Failed to summarize";
      analytics.trackSummaryError(videoId, errorMessage);
      setSummaryError(errorMessage);
    } finally {
      setSummarizing(false);
    }
  };

  const handleVpnSignInSuccess = () => {
    setShowVpnSignIn(false);
    // Refresh session state so UI reflects signed-in status
    refreshSubscription();
    // Retry the transcript fetch only if a URL was entered
    if (url.trim()) {
      const syntheticEvent = { preventDefault: () => {} } as React.FormEvent;
      handleSubmit(syntheticEvent);
    }
  };

  const formatTimestamp = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  // SRT timestamp format: 00:00:00,000
  const formatSrtTimestamp = (ms: number): string => {
    const totalSeconds = Math.floor(ms / 1000);
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;
    const milliseconds = ms % 1000;
    return `${hours.toString().padStart(2, "0")}:${minutes.toString().padStart(2, "0")}:${seconds.toString().padStart(2, "0")},${milliseconds.toString().padStart(3, "0")}`;
  };

  // VTT timestamp format: 00:00:00.000
  const formatVttTimestamp = (ms: number): string => {
    const totalSeconds = Math.floor(ms / 1000);
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;
    const milliseconds = ms % 1000;
    return `${hours.toString().padStart(2, "0")}:${minutes.toString().padStart(2, "0")}:${seconds.toString().padStart(2, "0")}.${milliseconds.toString().padStart(3, "0")}`;
  };

  const generateSrt = (): string => {
    return transcript
      .map((seg, index) => {
        const startMs = seg.offset;
        const endMs = seg.offset + seg.duration;
        return `${index + 1}\n${formatSrtTimestamp(startMs)} --> ${formatSrtTimestamp(endMs)}\n${seg.text}\n`;
      })
      .join("\n");
  };

  const generateVtt = (): string => {
    const cues = transcript
      .map((seg) => {
        const startMs = seg.offset;
        const endMs = seg.offset + seg.duration;
        return `${formatVttTimestamp(startMs)} --> ${formatVttTimestamp(endMs)}\n${seg.text}`;
      })
      .join("\n\n");
    return `WEBVTT\n\n${cues}`;
  };

  const getPlainText = (): string => {
    if (showTimestamps) {
      return transcript
        .map((seg) => `[${formatTimestamp(seg.offset / 1000)}] ${seg.text}`)
        .join("\n");
    }
    return transcript.map((seg) => seg.text).join(" ");
  };

  // Search functionality
  const getSearchMatches = (): Array<{
    segmentIndex: number;
    startIndex: number;
    endIndex: number;
  }> => {
    if (!searchQuery.trim()) return [];
    const query = searchQuery.toLowerCase();
    const matches: Array<{
      segmentIndex: number;
      startIndex: number;
      endIndex: number;
    }> = [];

    transcript.forEach((seg, segIndex) => {
      const text = seg.text.toLowerCase();
      let startIndex = 0;
      while ((startIndex = text.indexOf(query, startIndex)) !== -1) {
        matches.push({
          segmentIndex: segIndex,
          startIndex,
          endIndex: startIndex + query.length,
        });
        startIndex += 1;
      }
    });

    return matches;
  };

  const searchMatches = getSearchMatches();
  const totalMatches = searchMatches.length;

  const handleSearchChange = (value: string) => {
    setSearchQuery(value);
    setCurrentMatchIndex(0);
  };

  // Track search usage (debounced)
  useEffect(() => {
    if (!searchQuery.trim() || searchQuery.length < 2) return;
    const timer = setTimeout(() => {
      analytics.trackSearchTranscript(searchQuery, totalMatches);
    }, 1000);
    return () => clearTimeout(timer);
  }, [searchQuery, totalMatches]);

  // Scroll to current match when navigating
  useEffect(() => {
    if (currentMatchRef.current) {
      currentMatchRef.current.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
    }
  }, [currentMatchIndex]);

  const goToNextMatch = () => {
    if (totalMatches > 0) {
      setCurrentMatchIndex((prev) => (prev + 1) % totalMatches);
    }
  };

  const goToPrevMatch = () => {
    if (totalMatches > 0) {
      setCurrentMatchIndex((prev) => (prev - 1 + totalMatches) % totalMatches);
    }
  };

  const highlightText = (
    text: string,
    segmentIndex: number,
  ): React.ReactNode => {
    if (!searchQuery.trim()) return text;

    const segmentMatches = searchMatches.filter(
      (m) => m.segmentIndex === segmentIndex,
    );
    if (segmentMatches.length === 0) return text;

    const parts: React.ReactNode[] = [];
    let lastIndex = 0;

    segmentMatches.forEach((match, matchIdx) => {
      // Add text before match
      if (match.startIndex > lastIndex) {
        parts.push(text.slice(lastIndex, match.startIndex));
      }

      // Check if this is the current match
      const globalMatchIndex = searchMatches.findIndex(
        (m) =>
          m.segmentIndex === segmentIndex && m.startIndex === match.startIndex,
      );
      const isCurrentMatch = globalMatchIndex === currentMatchIndex;

      // Add highlighted match
      parts.push(
        <mark
          key={`${segmentIndex}-${matchIdx}`}
          ref={
            isCurrentMatch
              ? (currentMatchRef as React.RefObject<HTMLElement>)
              : undefined
          }
          className={`rounded px-0.5 ${
            isCurrentMatch
              ? "bg-orange-400 dark:bg-orange-500 text-white"
              : "bg-yellow-200 dark:bg-yellow-700"
          }`}
        >
          {text.slice(match.startIndex, match.endIndex)}
        </mark>,
      );
      lastIndex = match.endIndex;
    });

    // Add remaining text
    if (lastIndex < text.length) {
      parts.push(text.slice(lastIndex));
    }

    return <>{parts}</>;
  };

  const groupIntoParagraphs = (
    segments: TranscriptSegment[],
  ): TranscriptSegment[][] => {
    const paragraphs: TranscriptSegment[][] = [];
    let currentParagraph: TranscriptSegment[] = [];

    segments.forEach((segment, index) => {
      currentParagraph.push(segment);
      const text = segment.text;
      const isEndOfSentence = /[.!?]$/.test(text.trim());
      const wordCount = currentParagraph.reduce(
        (acc, seg) => acc + seg.text.split(" ").length,
        0,
      );

      if (
        (isEndOfSentence && wordCount >= 50) ||
        wordCount >= 100 ||
        index === segments.length - 1
      ) {
        paragraphs.push(currentParagraph);
        currentParagraph = [];
      }
    });

    if (currentParagraph.length > 0) {
      paragraphs.push(currentParagraph);
    }

    return paragraphs;
  };

  const handleCopy = async () => {
    await navigator.clipboard.writeText(getPlainText());
    analytics.trackCopyTranscript(wordCount, showTimestamps);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleTranscriptFeedback = (rating: "positive" | "negative") => {
    setTranscriptFeedbackRating(rating);
    // If positive, submit immediately
    if (rating === "positive" && videoId) {
      analytics.trackTranscriptFeedback(videoId, rating);
      setTranscriptFeedbackSubmitted(true);
    }
    // If negative, show text input (don't submit yet)
  };

  const handleTranscriptFeedbackSubmit = () => {
    if (!videoId || !transcriptFeedbackRating) return;
    analytics.trackTranscriptFeedback(
      videoId,
      transcriptFeedbackRating,
      transcriptFeedbackText || undefined,
    );
    setTranscriptFeedbackSubmitted(true);
  };

  const handleSummaryFeedback = (rating: "positive" | "negative") => {
    setSummaryFeedbackRating(rating);
    // If positive, submit immediately
    if (rating === "positive" && videoId) {
      analytics.trackSummaryFeedback(videoId, rating);
      setSummaryFeedbackSubmitted(true);
    }
    // If negative, show text input (don't submit yet)
  };

  const handleSummaryFeedbackSubmit = () => {
    if (!videoId || !summaryFeedbackRating) return;
    analytics.trackSummaryFeedback(
      videoId,
      summaryFeedbackRating,
      summaryFeedbackText || undefined,
    );
    setSummaryFeedbackSubmitted(true);
  };

  const handleDownload = (format: "txt" | "srt" | "vtt" = "txt") => {
    let content: string;
    let mimeType: string;
    let extension: string;

    switch (format) {
      case "srt":
        content = generateSrt();
        mimeType = "application/x-subrip";
        extension = "srt";
        break;
      case "vtt":
        content = generateVtt();
        mimeType = "text/vtt";
        extension = "vtt";
        break;
      default:
        content = getPlainText();
        mimeType = "text/plain";
        extension = "txt";
    }

    const blob = new Blob([content], { type: mimeType });
    const downloadUrl = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = downloadUrl;
    a.download = `transcript.${extension}`;
    a.click();
    URL.revokeObjectURL(downloadUrl);
    analytics.trackDownloadTranscript(wordCount, showTimestamps, format);
  };

  const handleBatchProcess = async () => {
    const urls = batchUrls
      .split("\n")
      .map((u) => u.trim())
      .filter((u) => u.length > 0);

    if (urls.length === 0) return;

    // Initialize results with pending status
    const initialResults = urls.map((url) => ({
      url,
      videoId: extractVideoId(url) || "",
      transcript: [] as TranscriptSegment[],
      status: "pending" as const,
    }));

    setBatchResults(initialResults);
    setBatchProcessing(true);

    // Process each URL sequentially
    for (let i = 0; i < urls.length; i++) {
      const url = urls[i];
      const videoId = extractVideoId(url);

      // Update status to loading
      setBatchResults((prev) =>
        prev.map((r, idx) => (idx === i ? { ...r, status: "loading" } : r)),
      );

      if (!videoId) {
        setBatchResults((prev) =>
          prev.map((r, idx) =>
            idx === i ? { ...r, status: "error", error: "Invalid URL" } : r,
          ),
        );
        continue;
      }

      try {
        const { response, data } = await fetchWithTurnstile(
          `/api/transcript?videoId=${videoId}`,
          {},
          getTurnstileToken,
        );

        if (!response.ok) {
          setBatchResults((prev) =>
            prev.map((r, idx) =>
              idx === i
                ? {
                    ...r,
                    status: "error",
                    error: data.error || "Failed to fetch",
                  }
                : r,
            ),
          );
          continue;
        }

        // Fetch title for successful transcripts
        const metadata = await fetchVideoMetadata(videoId);

        setBatchResults((prev) =>
          prev.map((r, idx) =>
            idx === i
              ? {
                  ...r,
                  status: "success",
                  transcript: data.transcript,
                  title: metadata.title || undefined,
                }
              : r,
          ),
        );
      } catch {
        setBatchResults((prev) =>
          prev.map((r, idx) =>
            idx === i ? { ...r, status: "error", error: "Network error" } : r,
          ),
        );
      }
    }

    setBatchProcessing(false);
  };

  const handleBatchDownloadAll = () => {
    const successfulResults = batchResults.filter(
      (r) => r.status === "success",
    );
    if (successfulResults.length === 0) return;

    const allText = successfulResults
      .map((r) => {
        const text = r.transcript.map((seg) => seg.text).join(" ");
        const videoLabel = r.title || r.videoId;
        return `=== ${videoLabel} ===\n\n${text}\n\n`;
      })
      .join("\n---\n\n");

    const blob = new Blob([allText], { type: "text/plain" });
    const downloadUrl = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = downloadUrl;
    a.download = "transcripts-batch.txt";
    a.click();
    URL.revokeObjectURL(downloadUrl);
  };

  const handleApiAccessClick = () => {
    analytics.trackApiAccessClick();
    // The developer API is live — send high-intent clicks to the real thing
    window.location.href = "/developers";
  };

  const handleFooterWaitlistClick = () => {
    analytics.trackWaitlistOpen("footer");
    setShowWaitlist(true);
  };

  const handleAutoTranslateClick = (targetLangName: string) => {
    analytics.trackAutoTranslateClick(targetLangName, videoId || undefined);
    setAutoTranslateTargetLang(targetLangName);
    setShowAutoTranslateModal(true);
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-slate-100 dark:from-slate-950 dark:to-slate-900">
      {process.env.NEXT_PUBLIC_TURNSTILE_SITE_KEY && (
        <>
          <Script
            src="https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit"
            strategy="afterInteractive"
          />
          <div id={turnstileContainerId} style={{ position: "absolute", visibility: "hidden", pointerEvents: "none" }} />
        </>
      )}
      <main className="mx-auto max-w-3xl px-4 py-12 sm:py-20">
        {/* Hero */}
        <div className="text-center mb-12">
          {/* Logo + Pro Badge */}
          <div className="flex items-center justify-center gap-3 mb-6">
            <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-blue-600 shadow-lg shadow-blue-600/25">
              <svg className="w-7 h-7" viewBox="0 0 32 32" fill="none" aria-hidden="true">
                <rect x="4" y="7" width="24" height="5" rx="2.5" fill="white" />
                <rect
                  x="4"
                  y="14"
                  width="18"
                  height="5"
                  rx="2.5"
                  fill="white"
                  fillOpacity="0.8"
                />
                <rect
                  x="4"
                  y="21"
                  width="12"
                  height="5"
                  rx="2.5"
                  fill="white"
                  fillOpacity="0.6"
                />
              </svg>
            </div>
            <ProBadge hasPro={hasPro} />
          </div>

          <h1 className="text-4xl sm:text-5xl font-extrabold text-slate-900 dark:text-white mb-4 tracking-tight">
            YouTube videos → <span className="text-blue-600">Text</span>
          </h1>

          <p className="text-lg text-slate-600 dark:text-slate-400 mb-4 max-w-xl mx-auto">
            Get a YouTube transcript instantly and let AI summarize it for you.
          </p>

          {/* Social Proof Counter */}
          <p className="text-sm text-slate-500 dark:text-slate-400 mb-8">
            <span className="font-semibold text-blue-600 dark:text-blue-400">
              127,000+
            </span>{" "}
            transcripts generated
          </p>

          {/* Mode Toggle */}
          <div className="flex justify-center mb-4">
            <div className="inline-flex rounded-lg bg-slate-100 dark:bg-slate-800 p-1">
              <button
                onClick={() => setBatchMode(false)}
                aria-pressed={!batchMode}
                className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
                  !batchMode
                    ? "bg-white dark:bg-slate-700 text-slate-900 dark:text-white shadow-sm"
                    : "text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white"
                }`}
              >
                Single Video
              </button>
              <button
                onClick={() => setBatchMode(true)}
                aria-pressed={batchMode}
                className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
                  batchMode
                    ? "bg-white dark:bg-slate-700 text-slate-900 dark:text-white shadow-sm"
                    : "text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white"
                }`}
              >
                Batch Mode
              </button>
            </div>
          </div>

          {/* Single Video Input */}
          {!batchMode && (
            <form onSubmit={handleSubmit} className="mb-6">
              <div className="flex w-full max-w-xl mx-auto shadow-xl shadow-slate-200/50 dark:shadow-slate-900/50 rounded-xl overflow-hidden">
                <input
                  type="text"
                  value={url}
                  onChange={(e) => setUrl(e.target.value)}
                  placeholder="Paste YouTube URL or video ID..."
                  aria-label="YouTube video URL or ID"
                  className="flex-1 px-5 py-4 text-base bg-white dark:bg-slate-800 border-0 focus:outline-none focus:ring-2 focus:ring-blue-600/20 text-slate-900 dark:text-white placeholder:text-slate-400"
                />
                <button
                  type="submit"
                  disabled={loading || !url}
                  className="px-6 py-4 cursor-pointer bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed text-white font-semibold transition-colors flex items-center gap-2"
                >
                  {loading ? (
                    <svg
                      className="w-5 h-5 animate-spin"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      />
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      />
                    </svg>
                  ) : (
                    "Get Transcript"
                  )}
                </button>
              </div>
            </form>
          )}

          {/* Batch Mode Input */}
          {batchMode && (
            <div className="mb-6 max-w-xl mx-auto">
              <textarea
                value={batchUrls}
                onChange={(e) => setBatchUrls(e.target.value)}
                placeholder="Paste YouTube URLs or video IDs (one per line)..."
                aria-label="YouTube video URLs or IDs"
                rows={5}
                className="w-full px-5 py-4 text-base bg-white dark:bg-slate-800 border-0 focus:outline-none focus:ring-2 focus:ring-blue-600/20 text-slate-900 dark:text-white placeholder:text-slate-400 rounded-xl shadow-xl shadow-slate-200/50 dark:shadow-slate-900/50 resize-none"
              />
              <button
                onClick={handleBatchProcess}
                disabled={batchProcessing || !batchUrls.trim()}
                className="w-full mt-3 px-6 py-4 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed text-white font-semibold rounded-xl transition-colors flex items-center justify-center gap-2"
              >
                {batchProcessing ? (
                  <>
                    <svg
                      className="w-5 h-5 animate-spin"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      />
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      />
                    </svg>
                    Processing...
                  </>
                ) : (
                  "Get All Transcripts"
                )}
              </button>
            </div>
          )}

          {/* Trust badges */}
          <div className="flex flex-wrap items-center justify-center gap-4 text-sm text-slate-500 dark:text-slate-400">
            <span className="flex items-center gap-1.5">
              <svg
                className="w-4 h-4 text-green-500"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  fillRule="evenodd"
                  d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                  clipRule="evenodd"
                />
              </svg>
              Free to use
            </span>
            <span className="flex items-center gap-1.5">
              <svg
                className="w-4 h-4 text-green-500"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  fillRule="evenodd"
                  d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                  clipRule="evenodd"
                />
              </svg>
              No account needed
            </span>
            <span className="flex items-center gap-1.5">
              <svg
                className="w-4 h-4 text-green-500"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  fillRule="evenodd"
                  d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                  clipRule="evenodd"
                />
              </svg>
              AI summaries
            </span>
            <span className="text-slate-300 dark:text-slate-600">|</span>
            <button
              onClick={handleApiAccessClick}
              className="flex items-center gap-1.5 text-orange-600 dark:text-orange-400 hover:text-orange-700 dark:hover:text-orange-300 font-medium transition-colors cursor-pointer"
            >
              <svg
                className="w-4 h-4"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4"
                />
              </svg>
              API Access
            </button>
          </div>
        </div>

        {/* Error */}
        {error && !batchMode && (
          <div role="alert" className="flex items-start gap-3 p-4 mb-6 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl text-red-700 dark:text-red-400">
            <svg
              className="w-5 h-5 flex-shrink-0 mt-0.5"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
            <div>
              <span>{error}</span>
              {errorHelpLink && (
                <Link
                  href={errorHelpLink}
                  className="ml-2 text-blue-600 dark:text-blue-400 hover:underline font-medium"
                >
                  Here&apos;s how to fix it →
                </Link>
              )}
              {!hasPro && (error.includes("No transcript available") || error.includes("no captions")) && (
                <button
                  onClick={() => {
                    setPricingSource("no_captions");
                    setShowPricing(true);
                  }}
                  className="mt-2 block text-sm font-medium text-blue-600 dark:text-blue-400 hover:underline"
                >
                  Upgrade to Pro for AI transcription of videos without captions →
                </button>
              )}
            </div>
          </div>
        )}

        {/* Batch Results */}
        {batchMode && batchResults.length > 0 && (
          <div className="mb-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-slate-900 dark:text-white">
                Results (
                {batchResults.filter((r) => r.status === "success").length}/
                {batchResults.length} successful)
              </h2>
              {batchResults.some((r) => r.status === "success") && (
                <button
                  onClick={handleBatchDownloadAll}
                  className="px-4 py-2 text-sm font-medium text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg transition-colors"
                >
                  Download All
                </button>
              )}
            </div>
            <div className="space-y-3">
              {batchResults.map((result, index) => (
                <div
                  key={index}
                  className="flex items-center gap-4 p-4 bg-white dark:bg-slate-800 rounded-xl shadow-sm"
                >
                  {result.videoId && (
                    <img
                      src={`https://img.youtube.com/vi/${result.videoId}/default.jpg`}
                      alt=""
                      className="w-16 h-12 object-cover rounded"
                    />
                  )}
                  <div className="flex-1 min-w-0">
                    {result.title ? (
                      <p className="text-sm font-medium text-slate-900 dark:text-white truncate">
                        {result.title}
                      </p>
                    ) : (
                      <p className="text-sm text-slate-600 dark:text-slate-400 truncate">
                        {result.url}
                      </p>
                    )}
                    {result.status === "success" && (
                      <p className="text-xs text-slate-500 dark:text-slate-500">
                        {result.transcript
                          .reduce(
                            (acc, seg) => acc + seg.text.split(/\s+/).length,
                            0,
                          )
                          .toLocaleString()}{" "}
                        words
                      </p>
                    )}
                    {result.status === "error" && (
                      <p className="text-xs text-red-500">{result.error}</p>
                    )}
                  </div>
                  <div className="flex-shrink-0">
                    {result.status === "pending" && (
                      <span className="text-slate-400">Waiting...</span>
                    )}
                    {result.status === "loading" && (
                      <svg
                        className="w-5 h-5 animate-spin text-blue-600"
                        fill="none"
                        viewBox="0 0 24 24"
                      >
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                        />
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                        />
                      </svg>
                    )}
                    {result.status === "success" && (
                      <svg
                        className="w-5 h-5 text-green-500"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path
                          fillRule="evenodd"
                          d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                          clipRule="evenodd"
                        />
                      </svg>
                    )}
                    {result.status === "error" && (
                      <svg
                        className="w-5 h-5 text-red-500"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path
                          fillRule="evenodd"
                          d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                          clipRule="evenodd"
                        />
                      </svg>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Video Preview */}
        {!batchMode && videoId && transcript.length > 0 && (
          <div className="bg-white dark:bg-slate-800 rounded-xl shadow-lg shadow-slate-200/50 dark:shadow-slate-900/50 p-4 mb-6">
            <div className="flex items-center gap-4">
              <a
                href={`https://youtube.com/watch?v=${videoId}`}
                target="_blank"
                rel="noopener noreferrer"
                className="flex-shrink-0"
              >
                <img
                  src={`https://img.youtube.com/vi/${videoId}/mqdefault.jpg`}
                  alt="Video thumbnail"
                  className="w-28 h-auto rounded-lg hover:opacity-90 transition-opacity"
                />
              </a>
              <div className="flex flex-wrap gap-4 text-sm text-slate-600 dark:text-slate-400">
                <div className="flex items-center gap-1.5">
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                    />
                  </svg>
                  {wordCount.toLocaleString()} words
                </div>
                <div className="flex items-center gap-1.5">
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                  {readingTime} min read
                </div>
                <div className="flex items-center gap-1.5">
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z"
                    />
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                  {videoDuration} min video
                </div>
                {/* Language selector or label */}
                {availableLanguages.length > 0 && (
                  <div className="flex items-center gap-1.5 text-slate-600 dark:text-slate-400">
                    <svg
                      className="w-4 h-4"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M3 5h12M9 3v2m1.048 9.5A18.022 18.022 0 016.412 9m6.088 9h7M11 21l5-10 5 10M12.751 5C11.783 10.77 8.07 15.61 3 18.129"
                      />
                    </svg>
                    <select
                      value={selectedLanguage || transcriptLanguage}
                      onChange={(e) => handleLanguageChange(e.target.value)}
                      disabled={loading}
                      className="text-sm bg-transparent border-none cursor-pointer hover:text-slate-900 dark:hover:text-white focus:outline-none disabled:opacity-50"
                    >
                      <optgroup label="Available Captions">
                        {availableLanguages.map((lang, index) => (
                          <option key={`${lang.code}-${index}`} value={lang.code}>
                            {lang.name}{lang.isAutoGenerated && !lang.name.includes('auto') ? ' (auto-generated)' : ''}
                          </option>
                        ))}
                      </optgroup>
                      <optgroup label="Translate to...">
                        {!transcriptLanguage.startsWith("en") && <option value="translate-English">English ✨</option>}
                        {!transcriptLanguage.startsWith("es") && <option value="translate-Spanish">Spanish ✨</option>}
                        {!transcriptLanguage.startsWith("fr") && <option value="translate-French">French ✨</option>}
                        {!transcriptLanguage.startsWith("de") && <option value="translate-German">German ✨</option>}
                        {!transcriptLanguage.startsWith("zh") && <option value="translate-Chinese">Chinese ✨</option>}
                        {!transcriptLanguage.startsWith("ja") && <option value="translate-Japanese">Japanese ✨</option>}
                        {!transcriptLanguage.startsWith("pt") && <option value="translate-Portuguese">Portuguese ✨</option>}
                        {!transcriptLanguage.startsWith("ru") && <option value="translate-Russian">Russian ✨</option>}
                      </optgroup>
                      {/* Re-transcribe the audio directly (AI) in a chosen language — recovers the
                          original language when captions are missing or YouTube mislabels them. */}
                      <optgroup label="Transcribe audio in...">
                        {TRANSCRIBE_LANGUAGES
                          .filter((l) => !availableLanguages.some((a) => a.code === l.code || a.code.startsWith(`${l.code}-`)))
                          .map((l) => (
                            <option key={`whisper-${l.code}`} value={l.code}>
                              {l.name} 🎙️
                            </option>
                          ))}
                      </optgroup>
                    </select>
                    {availableLanguages.some(l => l.isAutoGenerated) && (
                      <div className="relative group">
                        <svg
                          className="w-3.5 h-3.5 text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 cursor-help"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                          />
                        </svg>
                        <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-2 py-1 text-xs text-white bg-slate-800 dark:bg-slate-700 rounded whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
                          Auto-generated by YouTube
                        </div>
                      </div>
                    )}
                  </div>
                )}
                {loadingLanguages && (
                  <div className="flex items-center gap-1.5 text-slate-400">
                    <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                    </svg>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* AI Summary */}
        {!batchMode && transcript.length > 0 && (
          <div className="mb-6">
            {!summary && !summarizing && (
              <button
                onClick={handleSummarize}
                className="w-full cursor-pointer flex items-center justify-center gap-2 px-6 py-4 bg-violet-600 hover:bg-violet-700 text-white font-semibold rounded-xl transition-colors shadow-lg shadow-violet-600/25"
              >
                <svg
                  className="w-5 h-5"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M13 10V3L4 14h7v7l9-11h-7z"
                  />
                </svg>
                Summarize with AI
              </button>
            )}

            {summarizing && (
              <div className="w-full flex items-center justify-center gap-2 px-6 py-4 bg-violet-600/80 text-white font-semibold rounded-xl">
                <svg
                  className="w-5 h-5 animate-spin"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  />
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  />
                </svg>
                Generating summary...
              </div>
            )}

            {summaryError && (
              <div className="flex items-center gap-3 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl text-red-700 dark:text-red-400">
                <span>{summaryError}</span>
              </div>
            )}

            {summary && (
              <div className="bg-violet-50 dark:bg-violet-900/20 border border-violet-200 dark:border-violet-800 rounded-xl p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="flex items-center gap-2 text-violet-700 dark:text-violet-400 font-semibold">
                    <svg
                      className="w-5 h-5"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M13 10V3L4 14h7v7l9-11h-7z"
                      />
                    </svg>
                    AI Summary
                    {summaryTruncation && (
                      <span className="ml-2 text-xs font-normal text-amber-600 dark:text-amber-400">
                        (First {summaryTruncation.coveredMinutes} of{" "}
                        {summaryTruncation.totalMinutes} min)
                      </span>
                    )}
                  </h3>

                  {/* Inline Feedback */}
                  <div className="flex items-center gap-2">
                    {!summaryFeedbackSubmitted ? (
                      <>
                        <span className="text-xs text-violet-500 dark:text-violet-400">
                          Helpful?
                        </span>
                        <button
                          onClick={() => handleSummaryFeedback("positive")}
                          className={`p-1 rounded cursor-pointer transition-colors ${
                            summaryFeedbackRating === "positive"
                              ? "text-green-600 bg-green-100 dark:bg-green-900/30"
                              : "text-slate-400 hover:text-green-500 hover:bg-green-50 dark:hover:bg-green-900/20"
                          }`}
                          aria-label="Helpful"
                        >
                          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 10h4.764a2 2 0 011.789 2.894l-3.5 7A2 2 0 0115.263 21h-4.017c-.163 0-.326-.02-.485-.06L7 20m7-10V5a2 2 0 00-2-2h-.095c-.5 0-.905.405-.905.905 0 .714-.211 1.412-.608 2.006L7 11v9m7-10h-2M7 20H5a2 2 0 01-2-2v-6a2 2 0 012-2h2.5" />
                          </svg>
                        </button>
                        <button
                          onClick={() => handleSummaryFeedback("negative")}
                          className={`p-1 rounded cursor-pointer transition-colors ${
                            summaryFeedbackRating === "negative"
                              ? "text-red-600 bg-red-100 dark:bg-red-900/30"
                              : "text-slate-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20"
                          }`}
                          aria-label="Not helpful"
                        >
                          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 14H5.236a2 2 0 01-1.789-2.894l3.5-7A2 2 0 018.736 3h4.018a2 2 0 01.485.06l3.76.94m-7 10v5a2 2 0 002 2h.096c.5 0 .905-.405.905-.904 0-.715.211-1.413.608-2.008L17 13V4m-7 10h2m5-10h2a2 2 0 012 2v6a2 2 0 01-2 2h-2.5" />
                          </svg>
                        </button>
                      </>
                    ) : (
                      <span className="text-xs text-green-600 dark:text-green-400">
                        Thanks!
                      </span>
                    )}
                  </div>
                </div>

                {/* Negative feedback text input - shows below header when needed */}
                {summaryFeedbackRating === "negative" && !summaryFeedbackSubmitted && (
                  <div className="mb-4 p-3 bg-violet-100 dark:bg-violet-900/30 rounded-lg animate-in fade-in slide-in-from-top-2 duration-200">
                    <textarea
                      value={summaryFeedbackText}
                      onChange={(e) => setSummaryFeedbackText(e.target.value)}
                      placeholder="What could be better? (optional)"
                      rows={2}
                      className="w-full px-3 py-2 text-sm bg-white dark:bg-slate-800 border border-violet-200 dark:border-violet-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500/20 focus:border-violet-500 text-slate-900 dark:text-white placeholder:text-slate-400 resize-none"
                    />
                    <div className="flex justify-end mt-2">
                      <button
                        onClick={handleSummaryFeedbackSubmit}
                        className="px-4 py-1.5 text-sm font-medium text-white cursor-pointer bg-violet-600 hover:bg-violet-700 rounded-lg transition-colors"
                      >
                        Submit
                      </button>
                    </div>
                  </div>
                )}

                {/* Truncation Warning */}
                {summaryTruncation && !hasPro && (
                  <div className="mb-4 p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700 rounded-lg">
                    <div className="flex items-start gap-2">
                      <svg
                        className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                        />
                      </svg>
                      <div className="flex-1">
                        <p className="text-sm text-amber-800 dark:text-amber-200">
                          This video is {summaryTruncation.totalMinutes} minutes
                          long. The free tier summarizes the first ~
                          {summaryTruncation.coveredMinutes} minutes.
                        </p>
                        <button
                          onClick={() => {
                            setPricingSource("ai_summary");
                            setShowPricing(true);
                          }}
                          className="mt-2 text-sm font-medium text-amber-700 dark:text-amber-300 hover:text-amber-800 dark:hover:text-amber-200 underline"
                        >
                          Upgrade to Pro for full video summaries →
                        </button>
                      </div>
                    </div>
                  </div>
                )}

                <div className="prose prose-sm prose-slate dark:prose-invert max-w-none">
                  <ReactMarkdown
                    components={{
                      ul: ({ children }) => (
                        <ul className="list-disc pl-5 space-y-1 my-3">
                          {children}
                        </ul>
                      ),
                      ol: ({ children }) => (
                        <ol className="list-decimal pl-5 space-y-1 my-3">
                          {children}
                        </ol>
                      ),
                      li: ({ children }) => (
                        <li className="text-slate-700 dark:text-slate-300">
                          {children}
                        </li>
                      ),
                      p: ({ children }) => (
                        <p className="text-slate-700 dark:text-slate-300 mb-3">
                          {children}
                        </p>
                      ),
                      strong: ({ children }) => (
                        <strong className="font-semibold text-slate-900 dark:text-white">
                          {children}
                        </strong>
                      ),
                    }}
                  >
                    {summary}
                  </ReactMarkdown>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Transcript */}
        {!batchMode && transcript.length > 0 && (
          <div className="bg-white dark:bg-slate-800 rounded-xl shadow-lg shadow-slate-200/50 dark:shadow-slate-900/50 overflow-hidden">
            {/* Toolbar */}
            <div className="flex flex-col gap-3 px-4 py-3 border-b border-slate-200 dark:border-slate-700">
              {/* Search Bar */}
              <div className="flex items-center gap-2">
                <div className="relative flex-1">
                  <svg
                    className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    aria-hidden="true"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                    />
                  </svg>
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => handleSearchChange(e.target.value)}
                    placeholder="Search transcript..."
                    aria-label="Search transcript"
                    className="w-full pl-9 pr-3 py-2 text-sm bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 text-slate-900 dark:text-white placeholder:text-slate-400"
                  />
                </div>
                {searchQuery && (
                  <div className="flex items-center gap-1">
                    <span className="text-sm text-slate-500 dark:text-slate-400 whitespace-nowrap">
                      {totalMatches > 0
                        ? `${currentMatchIndex + 1}/${totalMatches}`
                        : "0 results"}
                    </span>
                    <button
                      onClick={goToPrevMatch}
                      disabled={totalMatches === 0}
                      className="p-1.5 text-slate-500 hover:text-slate-700 dark:hover:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 rounded disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                      aria-label="Previous match"
                    >
                      <svg
                        className="w-4 h-4"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M5 15l7-7 7 7"
                        />
                      </svg>
                    </button>
                    <button
                      onClick={goToNextMatch}
                      disabled={totalMatches === 0}
                      className="p-1.5 text-slate-500 hover:text-slate-700 dark:hover:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 rounded disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                      aria-label="Next match"
                    >
                      <svg
                        className="w-4 h-4"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M19 9l-7 7-7-7"
                        />
                      </svg>
                    </button>
                    <button
                      onClick={() => handleSearchChange("")}
                      className="p-1.5 text-slate-500 hover:text-slate-700 dark:hover:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 rounded transition-colors"
                      aria-label="Clear search"
                    >
                      <svg
                        className="w-4 h-4"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M6 18L18 6M6 6l12 12"
                        />
                      </svg>
                    </button>
                  </div>
                )}
              </div>

              {/* Options Row */}
              <div className="flex items-center justify-between">
                <label className="flex items-center gap-2 text-sm cursor-pointer text-slate-600 dark:text-slate-400">
                  <input
                    type="checkbox"
                    checked={showTimestamps}
                    onChange={(e) => {
                      setShowTimestamps(e.target.checked);
                      analytics.trackToggleTimestamps(e.target.checked);
                    }}
                    className="w-4 h-4 rounded border-slate-300 text-blue-600 focus:ring-blue-600"
                  />
                  Show timestamps
                </label>
                <div className="flex gap-2 items-center">
                  <button
                    onClick={handleCopy}
                    className="px-3 py-1.5 text-sm cursor-pointer text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition-colors"
                  >
                    {copied ? "Copied!" : "Copy"}
                  </button>

                  <div className="relative">
                    <button
                      onClick={() => setShowDownloadMenu(!showDownloadMenu)}
                      className="px-3 py-1.5 cursor-pointer text-sm text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition-colors flex items-center gap-1"
                    >
                      Download
                      <svg
                        className="w-4 h-4"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M19 9l-7 7-7-7"
                        />
                      </svg>
                    </button>
                    {showDownloadMenu && (
                      <div className="absolute right-0 mt-1 w-40 cursor-pointer bg-white dark:bg-slate-800 rounded-lg shadow-lg border border-slate-200 dark:border-slate-700 py-1 z-10">
                        <button
                          onClick={() => {
                            handleDownload("txt");
                            setShowDownloadMenu(false);
                          }}
                          className="w-full px-4 py-2 text-left text-sm cursor-pointer text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700"
                        >
                          Text (.txt)
                        </button>
                        <button
                          onClick={() => {
                            handleDownload("srt");
                            setShowDownloadMenu(false);
                          }}
                          className="w-full px-4 py-2 text-left text-sm cursor-pointer text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700"
                        >
                          Subtitles (.srt)
                        </button>
                        <button
                          onClick={() => {
                            handleDownload("vtt");
                            setShowDownloadMenu(false);
                          }}
                          className="w-full px-4 py-2 text-left text-sm text-slate-700 cursor-pointer dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700"
                        >
                          WebVTT (.vtt)
                        </button>
                      </div>
                    )}
                  </div>

                  {/* Remaining transcripts indicator (inline) */}
                  {!hasPro &&
                    remainingTranscripts !== null &&
                    remainingTranscripts <= 3 && (
                      <div className="flex items-center gap-2 text-sm">
                        <span className="text-slate-400">·</span>
                        {remainingTranscripts > 0 ? (
                          // Has free transcripts left
                          <>
                            <span
                              className={
                                remainingTranscripts <= 2
                                  ? "text-orange-600 dark:text-orange-400"
                                  : "text-slate-500 dark:text-slate-400"
                              }
                            >
                              {remainingTranscripts} free left
                            </span>
                            <button
                              onClick={() => {
                                setPricingSource("pro_button");
                                setShowPricing(true);
                              }}
                              className="text-purple-600 dark:text-purple-400 hover:underline cursor-pointer"
                            >
                              Upgrade →
                            </button>
                          </>
                        ) : creditBalance !== null && creditBalance > 0 ? (
                          // No free left, but has credits
                          <>
                            <span className="text-green-600 dark:text-green-400">
                              {creditBalance} credit
                              {creditBalance !== 1 ? "s" : ""} available
                            </span>
                            <button
                              onClick={() => {
                                setPricingSource("limit");
                                setShowPricing(true);
                              }}
                              className="text-green-600 dark:text-green-400 cursor-pointer hover:underline"
                            >
                              Buy more →
                            </button>
                          </>
                        ) : (
                          // No free left, no credits
                          <>
                            <span className="text-orange-600 dark:text-orange-400">
                              Daily limit reached
                            </span>
                            <button
                              onClick={() => {
                                setPricingSource("limit");
                                setShowPricing(true);
                              }}
                              className="text-purple-600 dark:text-purple-400 hover:underline cursor-pointer"
                            >
                              Upgrade →
                            </button>
                          </>
                        )}
                      </div>
                    )}
                </div>
              </div>
            </div>

            {/* Content */}
            <div className="p-6 max-h-[500px] overflow-y-auto">
              {showTimestamps ? (
                <div className="space-y-4">
                  {(() => {
                    let segmentCounter = 0;
                    return groupIntoParagraphs(transcript).map(
                      (paragraph, pIndex) => (
                        <div key={pIndex} className="space-y-1">
                          {paragraph.map((segment) => {
                            const currentIndex = segmentCounter++;
                            return (
                              <p
                                key={currentIndex}
                                className="text-slate-700 dark:text-slate-300"
                              >
                                <a
                                  href={`https://youtube.com/watch?v=${videoId}&t=${Math.floor(segment.offset / 1000)}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-blue-600 dark:text-blue-400 font-mono text-sm mr-2 hover:underline"
                                  title="Jump to this point in video"
                                >
                                  [{formatTimestamp(segment.offset / 1000)}]
                                </a>
                                {highlightText(segment.text, currentIndex)}
                              </p>
                            );
                          })}
                        </div>
                      ),
                    );
                  })()}
                </div>
              ) : (
                <div className="space-y-4">
                  {(() => {
                    let segmentCounter = 0;
                    return groupIntoParagraphs(transcript).map(
                      (paragraph, pIndex) => (
                        <p
                          key={pIndex}
                          className="text-slate-700 dark:text-slate-300 leading-relaxed"
                        >
                          {paragraph.map((seg, segIdx) => {
                            const currentIndex = segmentCounter++;
                            return (
                              <span key={currentIndex}>
                                {highlightText(seg.text, currentIndex)}
                                {segIdx < paragraph.length - 1 ? " " : ""}
                              </span>
                            );
                          })}
                        </p>
                      ),
                    );
                  })()}
                </div>
              )}
            </div>

            {/* Transcript Feedback */}
            <div className="px-6 py-4 border-t border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/50">
              <p className="text-xs text-slate-500 dark:text-slate-500 mb-3">
                Transcript sourced from YouTube captions. Accuracy depends on the original video.
              </p>
              {!transcriptFeedbackSubmitted ? (
                <div className="flex flex-col gap-3">
                  <div className="flex items-center gap-3">
                    <span className="text-sm text-slate-600 dark:text-slate-400">
                      Was this transcript accurate?
                    </span>
                    <div className="flex items-center gap-1">
                      <button
                        onClick={() => handleTranscriptFeedback("positive")}
                        className={`p-1.5 rounded transition-colors cursor-pointer ${
                          transcriptFeedbackRating === "positive"
                            ? "text-green-600 bg-green-100 dark:bg-green-900/30"
                            : "text-slate-400 hover:text-green-500 hover:bg-green-50 dark:hover:bg-green-900/20"
                        }`}
                        aria-label="Yes, accurate"
                      >
                        <svg
                          className="w-5 h-5"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M14 10h4.764a2 2 0 011.789 2.894l-3.5 7A2 2 0 0115.263 21h-4.017c-.163 0-.326-.02-.485-.06L7 20m7-10V5a2 2 0 00-2-2h-.095c-.5 0-.905.405-.905.905 0 .714-.211 1.412-.608 2.006L7 11v9m7-10h-2M7 20H5a2 2 0 01-2-2v-6a2 2 0 012-2h2.5"
                          />
                        </svg>
                      </button>
                      <button
                        onClick={() => handleTranscriptFeedback("negative")}
                        className={`p-1.5 rounded transition-colors cursor-pointer ${
                          transcriptFeedbackRating === "negative"
                            ? "text-red-600 bg-red-100 dark:bg-red-900/30"
                            : "text-slate-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20"
                        }`}
                        aria-label="Has issues"
                      >
                        <svg
                          className="w-5 h-5"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M10 14H5.236a2 2 0 01-1.789-2.894l3.5-7A2 2 0 018.736 3h4.018a2 2 0 01.485.06l3.76.94m-7 10v5a2 2 0 002 2h.096c.5 0 .905-.405.905-.904 0-.715.211-1.413.608-2.008L17 13V4m-7 10h2m5-10h2a2 2 0 012 2v6a2 2 0 01-2 2h-2.5"
                          />
                        </svg>
                      </button>
                    </div>
                  </div>
                  {/* Text input for negative feedback */}
                  {transcriptFeedbackRating === "negative" && (
                    <div className="flex flex-col gap-2 animate-in fade-in slide-in-from-top-2 duration-200">
                      <textarea
                        value={transcriptFeedbackText}
                        onChange={(e) =>
                          setTranscriptFeedbackText(e.target.value)
                        }
                        placeholder="What's wrong with the transcript? (optional)"
                        rows={2}
                        className="w-full px-3 py-2 text-sm bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 text-slate-900 dark:text-white placeholder:text-slate-400 resize-none"
                      />
                      <button
                        onClick={handleTranscriptFeedbackSubmit}
                        className="self-end px-4 py-1.5 text-sm font-medium text-white cursor-pointer bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors"
                      >
                        Submit
                      </button>
                    </div>
                  )}
                </div>
              ) : (
                <p className="text-sm text-blue-600 dark:text-blue-400 animate-in fade-in duration-200">
                  Thanks for your feedback!
                </p>
              )}
            </div>
          </div>
        )}

        {/* Empty State - Use Cases */}
        {!loading && transcript.length === 0 && !error && (
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-8">
            <div className="bg-white dark:bg-slate-800 rounded-xl p-6 text-center shadow-lg shadow-slate-200/50 dark:shadow-slate-900/50">
              <div className="w-12 h-12 rounded-full bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center mx-auto mb-3">
                <span className="text-2xl" aria-hidden="true">📚</span>
              </div>
              <h3 className="font-semibold text-slate-900 dark:text-white mb-1">
                Study faster
              </h3>
              <p className="text-sm text-slate-500 dark:text-slate-400">
                Turn lectures into searchable notes
              </p>
            </div>
            <div className="bg-white dark:bg-slate-800 rounded-xl p-6 text-center shadow-lg shadow-slate-200/50 dark:shadow-slate-900/50">
              <div className="w-12 h-12 rounded-full bg-rose-100 dark:bg-rose-900/30 flex items-center justify-center mx-auto mb-3">
                <span className="text-2xl" aria-hidden="true">✍️</span>
              </div>
              <h3 className="font-semibold text-slate-900 dark:text-white mb-1">
                Create content
              </h3>
              <p className="text-sm text-slate-500 dark:text-slate-400">
                Repurpose videos into blogs
              </p>
            </div>
            <div className="bg-white dark:bg-slate-800 rounded-xl p-6 text-center shadow-lg shadow-slate-200/50 dark:shadow-slate-900/50">
              <div className="w-12 h-12 rounded-full bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center mx-auto mb-3">
                <span className="text-2xl" aria-hidden="true">🎙️</span>
              </div>
              <h3 className="font-semibold text-slate-900 dark:text-white mb-1">
                Most videos
              </h3>
              <p className="text-sm text-slate-500 dark:text-slate-400">
                Even videos without captions (up to 2 hrs)
              </p>
            </div>
          </div>
        )}

        {/* Demo Section - Show what output looks like */}
        {!loading && transcript.length === 0 && !error && (
          <div className="mt-12 bg-white dark:bg-slate-800 rounded-xl shadow-lg shadow-slate-200/50 dark:shadow-slate-900/50 overflow-hidden">
            {/* Demo Toolbar with Search */}
            <div className="flex flex-col gap-3 px-4 py-3 border-b border-slate-200 dark:border-slate-700">
              {/* Demo Search Bar */}
              <div className="flex items-center gap-2">
                <div className="relative flex-1">
                  <svg
                    className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                    />
                  </svg>
                  <input
                    type="text"
                    defaultValue="posture"
                    readOnly
                    className="w-full pl-9 pr-3 py-2 text-sm bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-lg text-slate-900 dark:text-white cursor-default"
                  />
                </div>
                <div className="flex items-center gap-1">
                  <span className="text-sm text-slate-500 dark:text-slate-400 whitespace-nowrap">
                    1/2
                  </span>
                  <button className="p-1.5 text-slate-400 rounded cursor-default">
                    <svg
                      className="w-4 h-4"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M5 15l7-7 7 7"
                      />
                    </svg>
                  </button>
                  <button className="p-1.5 text-slate-400 rounded cursor-default">
                    <svg
                      className="w-4 h-4"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M19 9l-7 7-7-7"
                      />
                    </svg>
                  </button>
                </div>
              </div>
              {/* Demo Options Row */}
              <div className="flex items-center justify-between">
                <span className="text-sm text-slate-600 dark:text-slate-400">
                  Example output
                </span>
                <span className="text-xs text-slate-400 dark:text-slate-500">
                  From a TED Talk
                </span>
              </div>
            </div>
            <div className="p-6 max-h-[200px] overflow-hidden relative">
              <div className="space-y-3 text-sm text-slate-700 dark:text-slate-300">
                <p className="leading-relaxed">
                  <span className="text-blue-600 dark:text-blue-400 font-mono text-xs mr-2">
                    [0:00]
                  </span>
                  So I want to start by offering you a free no-tech life hack,
                  and all it requires of you is this: that you change your{" "}
                  <mark className="rounded px-0.5 bg-orange-400 dark:bg-orange-500 text-white">
                    posture
                  </mark>{" "}
                  for two minutes.
                </p>
                <p className="leading-relaxed">
                  <span className="text-blue-600 dark:text-blue-400 font-mono text-xs mr-2">
                    [0:14]
                  </span>
                  But before I give it away, I want to ask you to right now do a
                  little audit of your body.
                </p>
                <p className="leading-relaxed">
                  <span className="text-blue-600 dark:text-blue-400 font-mono text-xs mr-2">
                    [0:22]
                  </span>
                  So how many of you are sort of making yourselves smaller?
                  Maybe you&apos;re hunching, crossing your legs, maybe wrapping
                  your ankles...
                </p>
              </div>
              {/* Fade overlay */}
              <div className="absolute bottom-0 left-0 right-0 h-16 bg-gradient-to-t from-white dark:from-slate-800 to-transparent pointer-events-none" />
            </div>
            <div className="px-4 py-3 bg-slate-50 dark:bg-slate-800/50 border-t border-slate-200 dark:border-slate-700 text-center">
              <span className="text-xs text-slate-500 dark:text-slate-400">
                Paste any YouTube URL above to get started
              </span>
            </div>
          </div>
        )}

        {/* Footer */}
        <footer className="mt-16 text-center text-sm text-slate-500 dark:text-slate-400 space-y-3">
          <div className="flex items-center justify-center gap-4 flex-wrap">
            <a
              href="/blog"
              className="hover:text-slate-700 dark:hover:text-slate-300 transition-colors"
            >
              Blog
            </a>
            <span className="text-slate-300 dark:text-slate-600">·</span>
            <a
              href="/privacy"
              className="hover:text-slate-700 dark:hover:text-slate-300 transition-colors"
            >
              Privacy
            </a>
            <span className="text-slate-300 dark:text-slate-600">·</span>
            <a
              href="/cancel"
              className="hover:text-slate-700 dark:hover:text-slate-300 transition-colors"
            >
              Cancel subscription
            </a>
            <span className="text-slate-300 dark:text-slate-600">·</span>
            <button
              onClick={() => {
                analytics.trackFeedbackClick();
                setFeedbackDefaultTab("general");
                setShowFeedbackModal(true);
              }}
              className="hover:text-slate-700 dark:hover:text-slate-300 transition-colors cursor-pointer"
            >
              Feedback
            </button>
            <span className="text-slate-300 dark:text-slate-600">·</span>
            <button
              onClick={() => {
                setFeedbackDefaultTab("bug");
                setShowFeedbackModal(true);
              }}
              className="hover:text-slate-700 dark:hover:text-slate-300 transition-colors cursor-pointer"
            >
              Having an issue? Let us know
            </button>
            {!hasPro && (
              <>
                <span className="text-slate-300 dark:text-slate-600">·</span>
                <button
                  onClick={() => {
                    setPricingSource("manual");
                    setShowPricing(true);
                  }}
                  className="text-purple-600 dark:text-purple-400 hover:text-purple-700 dark:hover:text-purple-300 transition-colors font-medium cursor-pointer"
                >
                  Pro
                </button>
              </>
            )}
          </div>
          {hasPro ? (
            <div className="mt-4">
              {/* Cancellation notice */}
              {subscription?.cancelAtPeriodEnd &&
                subscription?.currentPeriodEnd &&
                (() => {
                  const endDate = new Date(subscription.currentPeriodEnd);
                  const hasEnded = endDate < new Date();
                  return (
                    <div
                      className={`mb-4 inline-flex items-center gap-2 px-4 py-2 border rounded-lg ${
                        hasEnded
                          ? "bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-700"
                          : "bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-700"
                      }`}
                    >
                      <svg
                        className={`w-4 h-4 ${hasEnded ? "text-red-500" : "text-amber-500"}`}
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                        />
                      </svg>
                      <span
                        className={
                          hasEnded
                            ? "text-red-700 dark:text-red-300"
                            : "text-amber-700 dark:text-amber-300"
                        }
                      >
                        {hasEnded ? "Ended" : "Ends"}{" "}
                        {endDate.toLocaleDateString()}
                      </span>
                      <button
                        onClick={openPortal}
                        className={`ml-2 px-3 py-1 text-white text-xs font-medium rounded-md transition-colors ${
                          hasEnded
                            ? "bg-red-600 hover:bg-red-700"
                            : "bg-amber-600 hover:bg-amber-700"
                        }`}
                      >
                        Resubscribe
                      </button>
                    </div>
                  );
                })()}

              {/* Account actions */}
              <div className="flex items-center justify-center gap-3">
                <button
                  onClick={openPortal}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white font-medium rounded-lg transition-colors"
                >
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"
                    />
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                    />
                  </svg>
                  Manage subscription
                </button>
                <button
                  onClick={logout}
                  className="text-xs text-slate-400 dark:text-slate-500 hover:text-slate-600 dark:hover:text-slate-300 transition-colors"
                >
                  Switch account
                </button>
              </div>
            </div>
          ) : (
            <p className="mt-2">
              {/* Only show remaining count in footer when no transcript loaded (experiment indicators handle it otherwise) */}
              {remainingTranscripts < 3 && transcript.length === 0 && (
                <>
                  <span className="text-blue-600 dark:text-blue-400 font-medium">
                    {remainingTranscripts} free transcripts
                  </span>{" "}
                  remaining today.{" "}
                  <button
                    onClick={() => {
                      setPricingSource("pro_button");
                      setShowPricing(true);
                    }}
                    className="text-purple-600 dark:text-purple-400 hover:underline font-medium cursor-pointer"
                  >
                    Upgrade to Pro
                  </button>
                  <span className="mx-2 text-slate-400">|</span>
                </>
              )}
              {authenticated ? (
                <button
                  onClick={logout}
                  className="text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200 hover:underline cursor-pointer"
                >
                  Sign out{sessionEmail ? ` (${sessionEmail})` : ""}
                </button>
              ) : (
                <button
                  onClick={() => setShowRestore(true)}
                  className="text-slate-600 dark:text-slate-300 hover:underline cursor-pointer"
                >
                  Already Pro? Restore access
                </button>
              )}
            </p>
          )}
        </footer>
      </main>

      {/* Waitlist Modal */}
      <WaitlistModal
        isOpen={showWaitlist}
        onClose={() => setShowWaitlist(false)}
        remainingTranscripts={remainingTranscripts}
        hasPro={hasPro}
        onOpenPricingModal={() => {
          setPricingSource("manual");
          setShowPricing(true);
        }}
      />

      {/* Pricing Modal */}
      <PricingModal
        isOpen={showPricing}
        onClose={() => setShowPricing(false)}
        source={pricingSource}
      />

      {/* Restore Modal */}
      <RestoreModal
        isOpen={showRestore}
        onClose={() => setShowRestore(false)}
        onRestored={() => {
          setShowRestore(false);
          refreshSubscription();
        }}
      />

      {/* VPN Sign-In Modal */}
      <VpnSignInModal
        isOpen={showVpnSignIn}
        onClose={() => setShowVpnSignIn(false)}
        onSignedIn={handleVpnSignInSuccess}
      />

      {/* Feedback Modal */}
      <FeedbackModal
        isOpen={showFeedbackModal}
        onClose={() => setShowFeedbackModal(false)}
        defaultTab={feedbackDefaultTab}
      />

      {/* Auto-Translate Coming Soon Modal */}
      <AutoTranslateModal
        isOpen={showAutoTranslateModal}
        onClose={() => setShowAutoTranslateModal(false)}
        targetLanguage={autoTranslateTargetLang}
      />

      {/* Handle restored query param (wrapped in Suspense for useSearchParams) */}
      <Suspense fallback={null}>
        <RestoredNotification onRestored={handleRestored} />
      </Suspense>

      {/* Restored Success Notification */}
      {restoredNotification && (
        <div className="fixed bottom-4 right-4 z-50 animate-in slide-in-from-bottom-4 fade-in duration-300">
          <div className="bg-green-600 text-white px-6 py-3 rounded-lg shadow-lg flex items-center gap-3">
            <svg
              className="w-5 h-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M5 13l4 4L19 7"
              />
            </svg>
            <span className="font-medium">Welcome back!</span>
            <button
              onClick={() => setRestoredNotification(false)}
              className="ml-2 text-green-200 hover:text-white"
            >
              <svg
                className="w-4 h-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>
        </div>
      )}

      {/* Credit Used Notification */}
      {creditUsedNotification && (
        <div className="fixed bottom-4 right-4 z-50 animate-in slide-in-from-bottom-4 fade-in duration-300">
          <div className="bg-emerald-600 text-white px-6 py-3 rounded-lg shadow-lg flex items-center gap-3">
            <svg
              className="w-5 h-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
            <span className="font-medium">
              1 credit used · {creditBalance} remaining
            </span>
            <button
              onClick={() => setCreditUsedNotification(false)}
              className="ml-2 text-emerald-200 hover:text-white"
            >
              <svg
                className="w-4 h-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>
        </div>
      )}

      {/* Sticky Footer Upgrade Indicator */}
      {!hasPro &&
        transcript.length > 0 &&
        remainingTranscripts !== null &&
        remainingTranscripts <= 3 && (
          <div className="fixed bottom-0 left-0 right-0 z-40 bg-white/95 dark:bg-slate-800/95 backdrop-blur-sm border-t border-slate-200/80 dark:border-slate-700/80 shadow-md">
            <div className="max-w-3xl mx-auto px-4 py-2 flex items-center justify-between">
              <div className="flex items-center gap-3">
                {remainingTranscripts > 0 ? (
                  <span
                    className={`text-sm font-medium ${remainingTranscripts <= 2 ? "text-orange-600 dark:text-orange-400" : "text-slate-600 dark:text-slate-400"}`}
                  >
                    {remainingTranscripts} free transcript
                    {remainingTranscripts !== 1 ? "s" : ""} left today
                  </span>
                ) : creditBalance !== null && creditBalance > 0 ? (
                  <span className="text-green-600 dark:text-green-400 text-sm font-medium">
                    {creditBalance} credit{creditBalance !== 1 ? "s" : ""}{" "}
                    available
                  </span>
                ) : (
                  <span className="text-orange-600 dark:text-orange-400 text-sm font-medium">
                    Daily limit reached
                  </span>
                )}
              </div>
              {remainingTranscripts === 0 &&
              creditBalance !== null &&
              creditBalance > 0 ? (
                <button
                  onClick={() => {
                    setPricingSource("limit");
                    setShowPricing(true);
                  }}
                  className="px-3 py-1.5 bg-green-600 hover:bg-green-700 text-white cursor-pointer text-xs font-medium rounded-md transition-colors"
                >
                  Buy Credits
                </button>
              ) : (
                <button
                  onClick={() => {
                    setPricingSource("pro_button");
                    setShowPricing(true);
                  }}
                  className="px-3 py-1.5 bg-purple-600 hover:bg-purple-700 text-white text-xs cursor-pointer font-medium rounded-md transition-colors"
                >
                  Get Unlimited Transcripts
                </button>
              )}
            </div>
          </div>
        )}
    </div>
  );
}
